import { matchPath } from 'react-router-dom';
import { ISSUE_LABELS } from '../constants/jobModel';

/**
 * Main sidebar destinations — clicking these resets the header back-stack
 * so titles do not keep a previous building (e.g. "Building #25 Registered Company").
 */
export const PRIMARY_NAV_PATHS = [
  '/',
  '/schedule',
  '/buildings',
  '/chiller',
  '/jobs',
  '/repairing',
  '/people',
  '/network',
  '/application',
  '/documents',
  '/settings',
];

export function isPrimaryNavPath(pathname) {
  const path = pathname || '/';
  return PRIMARY_NAV_PATHS.some((href) => path === href);
}

/**
 * Breadcrumb items: `to` null = current page (not a link). Order: specific paths first (matchPath end: true).
 */
const CONFIG = [
  {
    path: '/application/amend-ss-site-visit',
    build: () => [
      { label: 'Application', to: '/application' },
      { label: 'Amend CS List', to: '/application/amend-compliance-schedules' },
      { label: 'SS workflow', to: null },
    ],
  },
  {
    path: '/application/amend-compliance-schedules',
    build: () => [
      { label: 'Application', to: '/application' },
      { label: 'Amend CS List', to: null },
    ],
  },
  {
    path: '/application/apply-bwof',
    build: () => [
      { label: 'Application', to: '/application' },
      { label: 'Apply BWOF', to: null },
    ],
  },
  {
    path: '/application',
    build: () => [{ label: 'Application', to: null }],
  },
  {
    path: '/settings/db/structure/:tableName',
    build: (params) => [
      { label: 'Settings', to: '/settings' },
      { label: 'DB Setting', to: '/settings/db' },
      { label: params.tableName || 'Table', to: null },
    ],
  },
  {
    path: '/settings/checklists',
    build: () => [
      { label: 'Settings', to: '/settings' },
      { label: 'Checklist templates', to: null },
    ],
  },
  {
    path: '/settings/mail',
    build: () => [
      { label: 'Settings', to: '/settings' },
      { label: 'Mail', to: null },
    ],
  },
  {
    path: '/settings/db',
    build: () => [
      { label: 'Settings', to: '/settings' },
      { label: 'DB Setting', to: null },
    ],
  },
  {
    path: '/settings',
    build: () => [{ label: 'Settings', to: null }],
  },
  {
    path: '/documents',
    build: () => [{ label: 'Company files', to: null }],
  },
  {
    path: '/buildings/:id/paperwork',
    build: (p) => [
      { label: 'Buildings', to: '/buildings' },
      { label: 'Building', to: `/buildings/${p.id}` },
      { label: 'Paperwork', to: null },
    ],
  },
  {
    path: '/buildings/:id/documents',
    build: (p) => [
      { label: 'Buildings', to: '/buildings' },
      { label: 'Building', to: `/buildings/${p.id}` },
      { label: 'Documents', to: null },
    ],
  },
  {
    path: '/buildings/:id/mandates/:mandateId/edit',
    build: ({ id }) => [
      { label: 'Buildings', to: '/buildings' },
      { label: 'Building', to: id ? `/buildings/${id}` : null },
      { label: 'Edit IQP partners', to: null },
    ],
  },
  {
    path: '/buildings/:id/mandates/new',
    build: ({ id }) => [
      { label: 'Buildings', to: '/buildings' },
      { label: 'Building', to: id ? `/buildings/${id}` : null },
      { label: 'IQP partners', to: null },
    ],
  },
  {
    path: '/buildings/:id/checklists',
    build: (p) => [
      { label: 'Buildings', to: '/buildings' },
      { label: 'Building', to: `/buildings/${p.id}` },
      { label: 'Checklists', to: null },
    ],
  },
  {
    path: '/buildings/new',
    build: () => [
      { label: 'Buildings', to: '/buildings' },
      { label: 'New building', to: null },
    ],
  },
  {
    path: '/buildings/:id/edit',
    build: (p) => [
      { label: 'Buildings', to: '/buildings' },
      { label: 'Building', to: `/buildings/${p.id}` },
      { label: 'Edit', to: null },
    ],
  },
  {
    path: '/buildings/:id/order',
    build: (p) => [
      { label: 'Buildings', to: '/buildings' },
      { label: 'Building', to: `/buildings/${p.id}` },
      { label: ISSUE_LABELS.pageTitle, to: null },
    ],
  },
  {
    path: '/buildings/:id/compliance-schedule',
    build: (p) => [
      { label: 'Buildings', to: '/buildings' },
      { label: 'Building', to: `/buildings/${p.id}` },
      { label: 'Compliance Schedule', to: null },
    ],
  },
  {
    path: '/buildings/:id',
    build: (p) => [
      { label: 'Buildings', to: '/buildings' },
      { label: 'Building', to: null },
    ],
  },
  {
    path: '/buildings',
    build: () => [{ label: 'Buildings', to: null }],
  },
  {
    path: '/jobs/new',
    build: () => [
      { label: 'Inspections', to: '/jobs' },
      { label: 'New inspection', to: null },
    ],
  },
  {
    path: '/jobs/:id',
    build: (p) => [
      { label: 'Inspections', to: '/jobs' },
      { label: `Inspection #${p.id}`, to: null },
    ],
  },
  {
    path: '/jobs',
    build: () => [{ label: 'Inspections', to: null }],
  },
  {
    path: '/workers/new',
    build: () => [
      { label: 'Staff', to: '/people' },
      { label: 'New worker', to: null },
    ],
  },
  {
    path: '/workers/:id/edit',
    build: (p) => [
      { label: 'Staff', to: '/people' },
      { label: `Worker #${p.id}`, to: `/workers/${p.id}` },
      { label: 'Edit', to: null },
    ],
  },
  {
    path: '/workers/:id',
    build: (p) => [
      { label: 'Staff', to: '/people' },
      { label: `Worker #${p.id}`, to: null },
    ],
  },
  {
    path: '/people',
    build: () => [{ label: 'Staff', to: null }],
  },
  {
    path: '/workers',
    build: () => [{ label: 'Staff', to: '/people' }],
  },
  {
    path: '/network',
    build: () => [{ label: 'Registered Company', to: null }],
  },
  {
    path: '/network/partners/new',
    build: () => [
      { label: 'Registered Company', to: '/network?tab=our-partner' },
      { label: 'Our Partner', to: '/network?tab=our-partner' },
      { label: 'IQP partners', to: null },
    ],
  },
  {
    path: '/network/partners/:partnerId',
    build: () => [
      { label: 'Registered Company', to: '/network?tab=our-partner' },
      { label: 'Our Partner', to: '/network?tab=our-partner' },
      { label: 'Partner', to: null },
    ],
  },
  {
    path: '/network/org/:orgId/buildings/:buildingId/ss/:ssCode',
    build: ({ orgId, buildingId }) => [
      { label: 'Registered Company', to: '/network?tab=agent' },
      { label: 'Company', to: orgId ? `/network/org/${orgId}` : '/network?tab=agent' },
      {
        label: 'Building SS',
        to: orgId && buildingId ? `/network/org/${orgId}/buildings/${buildingId}` : null,
      },
      { label: 'SS history', to: null },
    ],
  },
  {
    path: '/network/org/:orgId/buildings/:buildingId',
    build: ({ orgId }) => [
      { label: 'Registered Company', to: '/network?tab=agent' },
      { label: 'Company', to: orgId ? `/network/org/${orgId}` : '/network?tab=agent' },
      { label: 'Building SS history', to: null },
    ],
  },
  {
    path: '/network/org/:orgId',
    build: () => [
      { label: 'Registered Company', to: '/network?tab=agent' },
      { label: 'Company', to: null },
    ],
  },
  {
    path: '/agent/ss-only',
    build: () => [
      { label: 'Registered Company', to: '/network?tab=agent' },
      { label: 'Agent', to: '/network?tab=agent' },
      { label: 'SS-Only Agent', to: null },
    ],
  },
  {
    path: '/experts/new',
    build: () => [
      { label: 'Registered Company', to: '/network?tab=engineers' },
      { label: 'New expert', to: null },
    ],
  },
  {
    path: '/experts/:id/edit',
    build: (p) => [
      { label: 'Registered Company', to: '/network?tab=engineers' },
      { label: `Edit expert (#${p.id})`, to: null },
    ],
  },
  {
    path: '/partners',
    build: () => [{ label: 'Partners', to: null }],
  },
  {
    path: '/experts',
    build: () => [
      { label: 'Registered Company', to: '/network?tab=engineers' },
      { label: 'Amendment experts', to: null },
    ],
  },
  {
    path: '/site-inspection-checklist',
    build: () => [{ label: 'Site inspection checklist', to: null }],
  },
  {
    path: '/site-inspection',
    build: () => [{ label: 'Site inspection', to: null }],
  },
  {
    path: '/repairing',
    build: () => [{ label: 'Repairing', to: null }],
  },
  {
    path: '/schedule',
    build: () => [{ label: 'Schedule', to: null }],
  },
  {
    path: '/',
    build: () => [{ label: 'Dashboard', to: null }],
  },
];

const SEGMENT_LABELS = {
  application: 'Application',
  'apply-bwof': 'Apply BWOF',
  'amend-compliance-schedules': 'Amend CS List',
  'amend-ss-site-visit': 'SS workflow',
  settings: 'Settings',
  db: 'DB Setting',
  checklists: 'Checklist templates',
  mail: 'Mail',
  structure: 'Structure',
  buildings: 'Buildings',
  new: 'New',
  edit: 'Edit',
  order: 'Issue',
  jobs: 'Inspections',
  workers: 'Workers',
  experts: 'Amendment experts',
  'iqp-register': 'Registered IQP',
  network: 'Registered Company',
  partners: 'Partners',
  agent: 'Agent',
  'ss-only': 'SS-Only Agent',
  repairing: 'Repairing',
  schedule: 'Schedule',
  'site-inspection': 'Site inspection',
  'site-inspection-checklist': 'Site inspection checklist',
};

function humanizeSegment(seg) {
  if (/^\d+$/.test(seg)) return `#${seg}`;
  if (SEGMENT_LABELS[seg]) return SEGMENT_LABELS[seg];
  return seg
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/** Fallback when no CONFIG entry matches (e.g. new routes). */
function fallbackFromPathname(pathname) {
  if (pathname === '/' || pathname === '') {
    return [{ label: 'Dashboard', to: null }];
  }
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 0) return [{ label: 'Dashboard', to: null }];
  const crumbs = [];
  let acc = '';
  parts.forEach((p, i) => {
    acc += `/${p}`;
    const isLast = i === parts.length - 1;
    crumbs.push({
      label: humanizeSegment(p),
      to: isLast ? null : acc,
    });
  });
  return crumbs;
}

/**
 * @returns {{ label: string, to: string | null }[] | null}
 */
/** Ancestors only (excludes current page) ?? each item has `to` for navigation. */
export function getBreadcrumbParents(pathname) {
  const crumbs = getBreadcrumbs(pathname);
  if (!crumbs || crumbs.length < 2) return [];
  return crumbs.slice(0, -1).filter((c) => c.to);
}

export function getBreadcrumbs(pathname) {
  const path = pathname || '/';
  for (const entry of CONFIG) {
    const m = matchPath({ path: entry.path, end: true }, path);
    if (m) {
      const crumbs = entry.build(m.params || {});
      return Array.isArray(crumbs) && crumbs.length > 0 ? crumbs : null;
    }
  }
  return fallbackFromPathname(path);
}

/** Unique key for a *main* screen (pathname + significant search).
 * Minor header tabs (`?tab=`) are excluded ?? switching them must not push back history.
 */
export function getScreenKey(pathname, search = '') {
  const path = pathname || '/';
  const params = new URLSearchParams(
    search.startsWith('?') ? search.slice(1) : search || ''
  );
  params.delete('tab');
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

/**
 * Human-readable label for the current *main* screen (for back-button origin text).
 * Uses breadcrumb / main title only ?? not minor tab names (System Info, Agent, etc.).
 */
export function getScreenLabel(pathname, search = '') {
  const path = pathname || '/';
  const params = new URLSearchParams(
    search.startsWith('?') ? search.slice(1) : search || ''
  );
  const crumbs = getBreadcrumbs(path);
  let label = crumbs?.length ? crumbs[crumbs.length - 1].label : 'Page';

  if (
    path === '/buildings' &&
    (params.get('inactive') === '1' || params.get('trash') === '1')
  ) {
    label = 'Buildings (Inactive)';
  }
  if (path === '/buildings' && params.get('scope') === 'ss_only') {
    label = 'Buildings (SS only)';
  }

  const ss = params.get('ss');
  if (ss && /\/units\/?$/.test(path)) {
    label =
      params.get('setup') === 'list' ? 'Select SS Units Setting' : `${ss} units`;
  }

  return label;
}
